import { NounListViewPanel } from '@quoruum/frontend-common/components/NounListViewPanel';
import type { OrgStorage } from '@quoruum/frontend-common/lib/storage';
import { useUrlParam } from '@quoruum/frontend-common/hooks/useUrlParam';
import { isBookingSite, type BookingSite } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { CenterTabPanel } from './CenterTabPanel';
import {
  ATTRIBUTE_PARAM,
  DRAWER_TAB_PARAM,
  DrawerTab,
  SCRAPE_METADATA_SITE_PARAM,
  SELECTED_ID_PARAM,
} from './hotelMapShared';
import type { SelectedType } from './hotelMapShared';

const DEFAULT_SCRAPE_METADATA_SITE: BookingSite = 'BRAND';

const HOTEL_DRAWER_TABS: ReadonlyArray<DrawerTab> = [
  'HOTEL',
  'HOTEL_SETTINGS',
  'SCRAPE_METADATA',
  'STATUS',
  'RATES',
];
const CITY_DRAWER_TABS = ['CITY', 'CITY_STATUS'] as const;

function isHotelDrawerTab(value: string | undefined): value is (typeof HOTEL_DRAWER_TABS)[number] {
  return HOTEL_DRAWER_TABS.some((tab) => tab === value);
}

function isCityDrawerTab(value: string | undefined): value is (typeof CITY_DRAWER_TABS)[number] {
  return CITY_DRAWER_TABS.some((tab) => tab === value);
}

export function HotelExplorer({ storage }: { storage: OrgStorage }): ReactElement {
  const [selectedHotelIdParam, setSelectedId] = useUrlParam(SELECTED_ID_PARAM);
  const [drawerTabParam, setDrawerTabParam] = useUrlParam(DRAWER_TAB_PARAM);
  const [attributeParams] = useUrlParam(ATTRIBUTE_PARAM, { multiple: true });
  const [scrapeMetadataSiteParam, setScrapeMetadataSiteParam] = useUrlParam(
    SCRAPE_METADATA_SITE_PARAM
  );

  const selectedCityIdFromAttribute = attributeParams
    .map((attribute) => {
      const separatorIndex = attribute.indexOf(':');
      return separatorIndex > 0 && attribute.slice(0, separatorIndex) === 'CITY'
        ? attribute.slice(separatorIndex + 1)
        : undefined;
    })
    .find((cityId) => cityId);
  const selectedType: SelectedType | undefined = selectedHotelIdParam
    ? 'HOTEL'
    : selectedCityIdFromAttribute
      ? 'CITY'
      : undefined;
  const selectedId = selectedHotelIdParam ?? selectedCityIdFromAttribute;
  const activeDrawerTab: DrawerTab =
    selectedType === 'CITY'
      ? isCityDrawerTab(drawerTabParam)
        ? drawerTabParam
        : 'CITY'
      : isHotelDrawerTab(drawerTabParam)
        ? drawerTabParam
        : 'HOTEL';
  const scrapeMetadataSite: BookingSite =
    scrapeMetadataSiteParam && isBookingSite(scrapeMetadataSiteParam)
      ? scrapeMetadataSiteParam
      : DEFAULT_SCRAPE_METADATA_SITE;

  const previousSelectedIdRef = useRef(selectedHotelIdParam);

  useEffect(() => {
    const previousSelectedId = previousSelectedIdRef.current;
    previousSelectedIdRef.current = selectedHotelIdParam;
    if (
      previousSelectedId !== undefined &&
      selectedHotelIdParam !== undefined &&
      previousSelectedId !== selectedHotelIdParam
    ) {
      setDrawerTabParam('HOTEL', { replace: true });
    }
  }, [selectedHotelIdParam, setDrawerTabParam]);

  function handleCloseHotel(): void {
    setSelectedId(undefined, { replace: true, clear: [DRAWER_TAB_PARAM] });
  }

  function handleSelectHotel(hotelId: string): void {
    setSelectedId(hotelId, { replace: true, clear: [DRAWER_TAB_PARAM] });
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-4">
      <h2 className="text-xl font-semibold tracking-tight text-slate-900">Hotel Explorer</h2>
      <div className="min-h-0 flex-1">
        <NounListViewPanel
          selectedHotelId={selectedType === 'HOTEL' ? selectedId : undefined}
          onSelectHotel={handleSelectHotel}
          storage={storage}
        >
          {selectedId && selectedType ? (
            <CenterTabPanel
              selectedId={selectedId}
              selectedType={selectedType}
              onClose={handleCloseHotel}
              activeDrawerTab={activeDrawerTab}
              setActiveDrawerTab={(tab) => setDrawerTabParam(tab, { replace: true })}
              scrapeMetadataSite={scrapeMetadataSite}
              setScrapeMetadataSite={(site) => setScrapeMetadataSiteParam(site, { replace: true })}
            />
          ) : (
            <p className="text-sm text-slate-500">Select a hotel to view its details.</p>
          )}
        </NounListViewPanel>
      </div>
    </div>
  );
}
